import type { ComponentType, SVGProps } from 'react';
import { GeminiLogo } from '@/components/icons/GeminiLogo';
import { OpenAILogo } from '@/components/icons/OpenAILogo';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useModel } from './model-context';
import { getModel, MODELS, type ModelProvider, type ReasoningEffort } from './types';

const PROVIDERS: {
  id: ModelProvider;
  label: string;
  Logo: ComponentType<SVGProps<SVGSVGElement>>;
}[] = [
  { id: 'openai', label: 'OpenAI', Logo: OpenAILogo },
  { id: 'google', label: 'Google', Logo: GeminiLogo },
];

const EFFORT_LABELS: Record<ReasoningEffort, string> = {
  none: 'None',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export function ModelSelector() {
  const { modelId, reasoningEffort, setModelId, setReasoningEffort } = useModel();
  const efforts = getModel(modelId).reasoningEfforts;
  const showReasoning = efforts.length > 0;

  return (
    <TooltipProvider>
      <div data-testid="model-selector" className="flex flex-col gap-3">
        <div>
          <span
            id="model-label"
            className="mb-1 block text-[length:var(--text-xs)] text-[var(--text-secondary)]"
          >
            Model
          </span>
          <Select value={modelId} onValueChange={setModelId}>
            <SelectTrigger
              aria-labelledby="model-label"
              className="h-8 w-full border-[var(--input-border)] bg-[var(--input-bg)] text-sm text-[var(--text-primary)] hover:brightness-125 focus:ring-white/30 [&>svg]:text-[var(--text-secondary)]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent data-surface="contrast" className="min-w-[200px]">
              {PROVIDERS.map(({ id, label, Logo }) => (
                <SelectGroup key={id}>
                  <SelectLabel className="flex items-center gap-2 text-[length:var(--text-xs)] text-[var(--text-secondary)]">
                    <Logo className="h-3.5 w-3.5 shrink-0" />
                    {label}
                  </SelectLabel>
                  {MODELS.filter((model) => model.provider === id).map((model) => (
                    <Tooltip key={model.id}>
                      <TooltipTrigger asChild>
                        <SelectItem value={model.id}>
                          <span className="flex items-center gap-2">
                            <Logo className="h-3.5 w-3.5 shrink-0" />
                            {model.displayName}
                          </span>
                        </SelectItem>
                      </TooltipTrigger>
                      <TooltipContent
                        side="right"
                        sideOffset={8}
                        className="max-w-60 bg-[#1a1d21] text-white border border-white/10 shadow-lg"
                      >
                        {model.description}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div
          className="overflow-hidden transition-all duration-150 ease-in-out"
          style={{
            maxHeight: showReasoning ? 60 : 0,
            opacity: showReasoning ? 1 : 0,
          }}
        >
          <span
            id="reasoning-label"
            className="mb-1 block text-[length:var(--text-xs)] text-[var(--text-secondary)]"
          >
            Reasoning Effort
          </span>
          <Select value={reasoningEffort} onValueChange={setReasoningEffort}>
            <SelectTrigger
              aria-labelledby="reasoning-label"
              aria-hidden={!showReasoning}
              tabIndex={showReasoning ? 0 : -1}
              className="h-8 w-full border-[var(--input-border)] bg-[var(--input-bg)] text-sm text-[var(--text-primary)] hover:brightness-125 focus:ring-white/30 [&>svg]:text-[var(--text-secondary)]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent data-surface="contrast">
              {efforts.map((effort) => (
                <SelectItem key={effort} value={effort}>
                  {EFFORT_LABELS[effort]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </TooltipProvider>
  );
}
